// Profile Service - Handles user profile API calls
// Falls back to localStorage if API is unavailable (for demo/mock mode)

import { apiGet, apiPut, apiPost, apiDelete, apiPostMultipart } from '@/utils/api';

export interface UserProfile {
  // Identity fields
  nom?: string;
  prenom?: string;
  nomArabe?: string;
  prenomArabe?: string;
  cnie?: string;
  passport?: string;
  paysOrigine?: string;
  // Personal info
  email?: string;
  telephone?: string;
  phoneCountry?: string;
  sexe?: string;
  dateNaissance?: string;
  handicap?: string;
  // Address
  adresse?: string;
  ville?: string;
  codePostal?: string;
  // Emergency contact
  urgenceNom?: string;
  urgenceRelation?: string;
  urgencePhoneCountry?: string;
  urgenceNumero?: string;
  urgenceAdresse?: string;
  // Staff-specific professional info
  rib?: string;
  salaireMensuelNet?: string;
  fonction?: string;
  diplomeId?: string;
  dateRecrutement?: string;
  // Professor E-DEP fields
  statutProfesseur?: string;
  departementAttacheId?: string;
  estOriginaireEtablissementPublic?: string;
  etablissementOrigine?: string;
  gradeId?: string;
  specialite?: string;
  matieresEnseignees?: string;
  tauxHoraire?: string;
  // Staff who also teach (DG, DP, Chef de filière, etc. who are also professors)
  isAlsoProfessor?: boolean;
  // Parent/Guardian fields
  relationAvecEtudiant?: string;
  relationPrecision?: string;
  // Physical document tracking for STAFF
  physicalDocCin?: boolean;
  physicalDocPassport?: boolean;
  physicalDocCv?: boolean;
  physicalDocContratTravail?: boolean;
  physicalDocDiplome?: boolean;
  physicalDocAttestationRib?: boolean;
  physicalDocEngagementProfesseur?: boolean;
  physicalDocReglementInterieur?: boolean;
  physicalDocConsentement?: boolean;
  physicalDocAutorisationEnseigner?: boolean;
  // Physical document tracking for STUDENTS
  physicalDocStudentCin?: boolean;
  physicalDocStudentBac?: boolean;
  physicalDocStudentReleveBac?: boolean;
  physicalDocStudentDernierDiplome?: boolean;
  physicalDocStudentReleveDiplome?: boolean;
  physicalDocStudentReleveAnneesPrecedentes?: boolean;
  physicalDocStudentCv?: boolean;
  physicalDocStudentLettreMotivation?: boolean;
  // Status
  isComplete?: boolean;
  completedAt?: string;
  // Submission status - once submitted, profile is locked for students
  isSubmitted?: boolean;
  submittedAt?: string;
  submittedBy?: string; // User ID who submitted
  // Profile completion flag for access control (from User model)
  profileCompleted?: boolean;
  // Documents uploaded by staff
  documents?: Array<{
    documentType: string;
    fileName: string;
    fileUrl: string;
    fileSize?: number;
    mimeType?: string;
  }>;
}

export interface TenantInfo {
  currentAcademicYear?: string;
  engagementTemplateUrl?: string;
}

// Student data from the Student table (academic information)
export interface StudentData {
  id: string;
  studentCode?: string;
  firstName?: string;
  lastName?: string;
  firstNameArabic?: string;
  lastNameArabic?: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  placeOfBirth?: string;
  gender?: string;
  nationality?: string;
  cnie?: string;
  passportNumber?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  codeFiliereInscrit?: string;
  filiereInscrit?: string;
  diplomeInscrit?: string;
  niveauFormation?: number;
  niveauFormationInitial?: number;
  currentSemester?: number;
  enrollmentDate?: string;
  status?: string;
  numeroMassarCNE?: string;
  situation?: string;
  premiereAnneeInscription?: string;
  serieBaccalaureat?: string;
  anneeObtentionBac?: string;
  diplomeAcces?: string;
  specialiteDiplomeAcces?: string;
  mentionDiplomeAcces?: string;
  lieuObtentionDiplomeAcces?: string;
  resident?: string;
  fonctionnaire?: string;
  pourcentageBourse?: number;
  mobilite?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactRelation?: string;
  includeInEdep?: boolean;
  // Academic year assignment
  academicYearId?: string;
  academicYear?: AcademicYearInfo | null;
  // Campus affiliation
  campusId?: string;
  campus?: {
    id: string;
    name: string;
    city?: string;
  } | null;
}

// Linked student for parent users
export interface LinkedStudent {
  relationship: string;
  isPrimary: boolean;
  student: {
    id: string;
    studentCode?: string;
    firstName?: string;
    lastName?: string;
    firstNameArabic?: string;
    lastNameArabic?: string;
    email?: string;
    phone?: string;
    filiereInscrit?: string;
    diplomeInscrit?: string;
    niveauFormation?: number;
    status?: string;
    photoUrl?: string;
  };
}

// Campus affiliation for staff users
export interface UserCampusAffiliation {
  id: string;
  isPrimary: boolean;
  campus: {
    id: string;
    name: string;
    city?: string;
  };
}

// Academic year info
export interface AcademicYearInfo {
  id: string;
  name: string;
  isCurrent: boolean;
}

/** Outcome of a login-email change performed by an admin */
export interface EmailChangeResult {
  email: string;
  changed: boolean;
  verificationSent: boolean;
}

export interface UserProfileResponse {
  emailChange?: EmailChangeResult | null;
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: string;
  tenantId?: string;
  staffCode?: string; // Staff code for display (e.g., DG250001, PROF2500001)
  avatarUrl?: string;
  isAlsoProfessor?: boolean;
  isCreatedByStaff?: boolean; // True if parent/tuteur was created by staff - profile should be view-only
  userCampuses?: UserCampusAffiliation[]; // Campus affiliation for staff
  academicYear?: AcademicYearInfo | null; // Academic year assignment for staff
  profile: UserProfile | null;
  student?: StudentData | null;
  linkedStudents?: LinkedStudent[] | null; // For parent users
  tenantInfo?: TenantInfo | null;
  /**
   * Filière transfers recorded for this student. Only returned by the admin
   * endpoint — the student's own profile never carries it.
   */
  filiereChanges?: FiliereChangeRecord[];
}

/** An officially recorded filière transfer (PV de changement de filière). */
export interface FiliereChangeRecord {
  id: string;
  academicYearId: string | null;
  fromCodeFiliere: string | null;
  fromFiliereName: string | null;
  toCodeFiliere: string;
  toFiliereName: string | null;
  pvReference: string | null;
  pvDate: string;
  motif: string | null;
}

// Get saved user id from localStorage
function getUserId(): string | null {
  const saved = localStorage.getItem('nexedu_user');
  if (saved) {
    try {
      const user = JSON.parse(saved);
      return user.id;
    } catch {
      return null;
    }
  }
  return null;
}

// Local storage key for profile data (fallback for mock mode)
function getProfileStorageKey(userId: string): string {
  return `nexedu_profile_${userId}`;
}

// Fetch user profile
export async function fetchProfile(): Promise<UserProfileResponse | null> {
  const userId = getUserId();

  if (!userId) {
    console.warn('No user ID found');
    return null;
  }

  try {
    // Try to fetch from API first
    const data = await apiGet<UserProfileResponse>('/profile');
    // Cache in localStorage for offline access - include isAlsoProfessor from user level
    const cacheData = {
      ...(data.profile || {}),
      isAlsoProfessor: data.isAlsoProfessor,
    };
    localStorage.setItem(getProfileStorageKey(userId), JSON.stringify(cacheData));
    return data;
  } catch {
    // Fallback to localStorage if API unavailable
    const saved = localStorage.getItem(getProfileStorageKey(userId));
    const savedProfile = saved ? JSON.parse(saved) : null;

    // Get user data from localStorage
    const savedUser = localStorage.getItem('nexedu_user');
    if (savedUser) {
      const user = JSON.parse(savedUser);
      return {
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role,
        tenantId: user.tenantId,
        // Get isAlsoProfessor from cached profile (which now includes it) or from user
        isAlsoProfessor: savedProfile?.isAlsoProfessor ?? user.isAlsoProfessor,
        profile: savedProfile,
      };
    }

    return null;
  }
}

// Update user profile (full save)
export async function updateProfile(profileData: UserProfile): Promise<UserProfile> {
  const userId = getUserId();

  if (!userId) {
    throw new Error('User not authenticated');
  }

  try {
    // Try to update via API first
    const data = await apiPut<UserProfile>('/profile', profileData);
    // Update local cache
    localStorage.setItem(getProfileStorageKey(userId), JSON.stringify(data));
    return data;
  } catch (error) {
    // Log the error to help debug
    console.error('updateProfile API error:', error);
    // Fallback: Save to localStorage
    const isComplete = checkProfileComplete(profileData);
    const profileToSave = {
      ...profileData,
      isComplete,
      completedAt: isComplete ? new Date().toISOString() : undefined,
    };

    localStorage.setItem(getProfileStorageKey(userId), JSON.stringify(profileToSave));

    // Return the saved profile
    return profileToSave;
  }
}

// Save profile progress (partial save)
export async function saveProfileProgress(profileData: Partial<UserProfile>): Promise<UserProfile> {
  const userId = getUserId();

  if (!userId) {
    throw new Error('User not authenticated');
  }

  try {
    // Try to save via API first
    const data = await apiPost<UserProfile>('/profile/save-progress', profileData);
    // Update local cache
    const saved = localStorage.getItem(getProfileStorageKey(userId));
    const existing = saved ? JSON.parse(saved) : {};
    const merged = { ...existing, ...data };
    localStorage.setItem(getProfileStorageKey(userId), JSON.stringify(merged));
    return data;
  } catch (error) {
    // Log the error to help debug
    console.error('saveProfileProgress API error:', error);
    // Fallback: Merge with existing localStorage data
    const storageKey = getProfileStorageKey(userId);
    const saved = localStorage.getItem(storageKey);
    const existing = saved ? JSON.parse(saved) : {};
    const merged = { ...existing, ...profileData };
    localStorage.setItem(storageKey, JSON.stringify(merged));
    return merged;
  }
}

// ==================== STUDENT PROFILE FUNCTIONS ====================

/** "PV de changement de filière" recorded when a student switches filière. */
export interface FiliereChangePv {
  /** ISO date (YYYY-MM-DD) — defaults to the day the change is recorded. */
  date: string;
  motif?: string;
  /** Only present on records imported with one; the form does not ask for it. */
  reference?: string;
}

export interface StudentProfileData {
  // Identity
  nom?: string;
  prenom?: string;
  nomArabe?: string;
  prenomArabe?: string;
  cnie?: string;
  passport?: string;
  sexe?: string;
  dateNaissance?: string;
  paysOrigine?: string;
  handicap?: string;
  // Contact
  email?: string;
  telephone?: string;
  phoneCountry?: string;
  adresse?: string;
  // Emergency contact
  urgenceNom?: string;
  urgenceRelation?: string;
  urgencePhoneCountry?: string;
  urgenceNumero?: string;
  urgenceAdresse?: string;
  // Academic
  numeroMassarCNE?: string;
  // Academic enrollment (admin only)
  codeFiliereInscrit?: string;
  niveauFormation?: number;
  niveauFormationInitial?: number;
  situation?: string;
  premiereAnneeInscription?: string;
  /**
   * Required when `codeFiliereInscrit` moves the student to another filière:
   * E-DEP expects the "PV de changement de filière" in the student's file.
   * Administrative only — the student never sees it.
   */
  pvChangementFiliere?: FiliereChangePv;
  // Diploma access
  serieBaccalaureat?: string;
  anneeObtentionBac?: string;
  diplomeAcces?: string;
  specialiteDiplomeAcces?: string;
  mentionDiplomeAcces?: string;
  lieuObtentionDiplomeAcces?: string;
  // Status & Aids
  resident?: string;
  fonctionnaire?: string;
  pourcentageBourse?: number;
  mobilite?: string;
  // Physical document tracking (admin only)
  physicalDocStudentCin?: boolean;
  physicalDocStudentBac?: boolean;
  physicalDocStudentReleveBac?: boolean;
  physicalDocStudentDernierDiplome?: boolean;
  physicalDocStudentReleveDiplome?: boolean;
  physicalDocStudentReleveAnneesPrecedentes?: boolean;
  physicalDocStudentCv?: boolean;
  physicalDocStudentLettreMotivation?: boolean;
}

// Update student profile (full save)
export async function updateStudentProfile(profileData: StudentProfileData): Promise<UserProfileResponse | null> {
  const userId = getUserId();

  if (!userId) {
    throw new Error('User not authenticated');
  }

  return apiPut<UserProfileResponse>('/profile/student', profileData);
}

// Save student profile progress (partial save)
export async function saveStudentProfileProgress(profileData: Partial<StudentProfileData>): Promise<UserProfileResponse | null> {
  const userId = getUserId();

  if (!userId) {
    throw new Error('User not authenticated');
  }

  return apiPost<UserProfileResponse>('/profile/student/save-progress', profileData);
}

// Save staff profile progress (partial save)
export async function saveStaffProfileProgress(profileData: Partial<UserProfile>): Promise<UserProfileResponse | null> {
  const userId = getUserId();

  if (!userId) {
    throw new Error('User not authenticated');
  }

  try {
    const data = await apiPost<UserProfileResponse>('/profile/staff/save-progress', profileData);
    // Update local cache with the API response
    if (data && data.profile) {
      const storageKey = getProfileStorageKey(userId);
      const saved = localStorage.getItem(storageKey);
      const existing = saved ? JSON.parse(saved) : {};
      const merged = { ...existing, ...data.profile, isAlsoProfessor: data.isAlsoProfessor };
      localStorage.setItem(storageKey, JSON.stringify(merged));
    }
    return data;
  } catch (error) {
    console.error('saveStaffProfileProgress API error:', error);
    // Fallback: Merge with existing localStorage data
    const storageKey = getProfileStorageKey(userId);
    const saved = localStorage.getItem(storageKey);
    const existing = saved ? JSON.parse(saved) : {};
    const merged = { ...existing, ...profileData };
    localStorage.setItem(storageKey, JSON.stringify(merged));
    throw error; // Re-throw to let caller handle the error
  }
}

// Check if profile is complete
function checkProfileComplete(data: UserProfile): boolean {
  const requiredFields = ['sexe', 'dateNaissance', 'paysOrigine'];

  for (const field of requiredFields) {
    if (!data[field as keyof UserProfile]) {
      return false;
    }
  }

  // For Moroccans, CNIE is required; for foreigners, passport is required
  if (data.paysOrigine === 'MA') {
    if (!data.cnie) return false;
  } else {
    if (!data.passport) return false;
  }

  return true;
}

// Get cached profile (sync, from localStorage only)
export function getCachedProfile(): UserProfile | null {
  const userId = getUserId();

  if (!userId) {
    return null;
  }

  const storageKey = getProfileStorageKey(userId);
  const saved = localStorage.getItem(storageKey);

  if (saved) {
    return JSON.parse(saved);
  }

  return null;
}

// Clear profile cache
export function clearProfileCache(): void {
  const userId = getUserId();
  if (userId) {
    localStorage.removeItem(getProfileStorageKey(userId));
  }
}

// Submit profile (lock it for editing by students)
export async function submitProfile(): Promise<UserProfile> {
  const userId = getUserId();

  if (!userId) {
    throw new Error('User not authenticated');
  }

  const storageKey = getProfileStorageKey(userId);
  const saved = localStorage.getItem(storageKey);
  const existing = saved ? JSON.parse(saved) : {};

  const submittedProfile = {
    ...existing,
    isSubmitted: true,
    submittedAt: new Date().toISOString(),
    submittedBy: userId,
  };

  localStorage.setItem(storageKey, JSON.stringify(submittedProfile));
  return submittedProfile;
}

// Check if profile is submitted (locked)
export function isProfileSubmitted(): boolean {
  const profile = getCachedProfile();
  return profile?.isSubmitted === true;
}

// Unlock profile for editing (admin only)
export async function unlockProfile(): Promise<UserProfile> {
  const userId = getUserId();

  if (!userId) {
    throw new Error('User not authenticated');
  }

  const storageKey = getProfileStorageKey(userId);
  const saved = localStorage.getItem(storageKey);
  const existing = saved ? JSON.parse(saved) : {};

  const unlockedProfile = {
    ...existing,
    isSubmitted: false,
    submittedAt: undefined,
    submittedBy: undefined,
  };

  localStorage.setItem(storageKey, JSON.stringify(unlockedProfile));
  return unlockedProfile;
}

// ==================== ADMIN STUDENT PROFILE DIRECT EDIT ====================

/**
 * Admin: Get student profile for editing
 */
export async function adminGetStudentProfile(studentUserId: string): Promise<UserProfileResponse | null> {
  return apiGet<UserProfileResponse>(`/profile/admin/students/${studentUserId}`);
}

/**
 * Admin: Update student profile directly
 */
export async function adminUpdateStudentProfile(
  studentUserId: string,
  profileData: StudentProfileData,
): Promise<UserProfileResponse | null> {
  return apiPut<UserProfileResponse>(`/profile/admin/students/${studentUserId}`, profileData);
}

// Student document types (matching backend enum)
export type StudentDocumentType =
  | 'CIN_FRONT'
  | 'CIN_BACK'
  | 'PASSPORT'
  | 'PHOTO'
  | 'DIPLOME_BAC_SCAN'
  | 'RELEVES_NOTES_BAC'
  | 'DIPLOME_ACCES_SCAN'
  | 'RELEVES_NOTES_ACCES'
  | 'RELEVES_NOTES'
  | 'PV_CHANGEMENT_FILIERE'
  | 'CV'
  | 'LETTRE_MOTIVATION'
  // Staff document types (same upload endpoints)
  | 'CONTRAT_TRAVAIL'
  | 'DIPLOME_SCAN'
  | 'ATTESTATION_RIB'
  // Professor documents (E-DEP, renewable annually)
  | 'ENGAGEMENT_PROFESSEUR'
  | 'AUTORISATION_ENSEIGNER';

export interface StudentDocument {
  id: string;
  documentType: string;
  fileName: string;
  fileUrl: string;
  fileSize?: number;
  mimeType?: string;
  isVerified: boolean;
  createdAt: string;
}

/**
 * Admin: Upload a document for a student
 */
export async function adminUploadStudentDocument(
  studentUserId: string,
  documentType: StudentDocumentType,
  file: File,
): Promise<StudentDocument> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('documentType', documentType);

  return apiPostMultipart<StudentDocument>(`/profile/admin/students/${studentUserId}/documents`, formData);
}

/**
 * Admin: Delete a document from a student's profile
 */
export async function adminDeleteStudentDocument(
  studentUserId: string,
  documentId: string,
): Promise<{ success: boolean }> {
  return apiDelete<{ success: boolean }>(`/profile/admin/students/${studentUserId}/documents/${documentId}`);
}

/**
 * Admin: Get student documents
 */
export async function adminGetStudentDocuments(studentUserId: string): Promise<StudentDocument[]> {
  return apiGet<StudentDocument[]>(`/profile/admin/users/${studentUserId}/documents`);
}

// ==================== ADMIN STAFF PROFILE DIRECT EDIT ====================

export interface StaffProfileData {
  // Identity
  nom?: string;
  prenom?: string;
  nomArabe?: string;
  prenomArabe?: string;
  cnie?: string;
  passport?: string;
  sexe?: string;
  dateNaissance?: string;
  paysOrigine?: string;
  handicap?: string;
  // Contact
  email?: string;
  telephone?: string;
  phoneCountry?: string;
  adresse?: string;
  ville?: string;
  codePostal?: string;
  // Emergency contact
  urgenceNom?: string;
  urgenceRelation?: string;
  urgencePhoneCountry?: string;
  urgenceNumero?: string;
  urgenceAdresse?: string;
  // Professional
  dateRecrutement?: string;
  fonction?: string;
  diplomeId?: string;
  specialite?: string;
  matieresEnseignees?: string;
  statutProfesseur?: string;
  tauxHoraire?: string;
  gradeId?: string;
  estOriginaireEtablissementPublic?: string;
  etablissementOrigine?: string;
  departementAttacheId?: string;
  rib?: string;
  salaireMensuelNet?: string;
  // Also professor flag
  isAlsoProfessor?: boolean;
  // Campus and academic year
  campusIds?: string[];
  academicYearId?: string;
  // Payroll campus
  payrollCampusId?: string;
  // Physical document tracking
  physicalDocCin?: boolean;
  physicalDocPassport?: boolean;
  physicalDocCv?: boolean;
  physicalDocContratTravail?: boolean;
  physicalDocDiplome?: boolean;
  physicalDocAttestationRib?: boolean;
  physicalDocEngagementProfesseur?: boolean;
  physicalDocReglementInterieur?: boolean;
  physicalDocConsentement?: boolean;
  physicalDocAutorisationEnseigner?: boolean;
}

export interface StaffProfileResponse {
  emailChange?: EmailChangeResult | null;
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  phoneCountry?: string;
  role: string;
  staffCode?: string;
  isAlsoProfessor?: boolean;
  payrollCampusId?: string;
  profile: UserProfile | null;
  campuses: Array<{
    id: string;
    name: string;
    city?: string;
    isPrimary: boolean;
  }>;
  academicYear?: {
    id: string;
    name: string;
    isCurrent: boolean;
  } | null;
  // Effective per-year engagement status (professors only, null otherwise)
  professorEngagement?: {
    currentAcademicYear: string;
    hasDigitalForCurrentYear: boolean;
    documentUrl: string | null;
    physicalDocReceived: boolean;
  } | null;
  documents: StudentDocument[];
  nomenclature: {
    grades: Array<{ id: string; labelFr: string }>;
    departments: Array<{ id: string; labelFr: string }>;
    diplomes: Array<{ id: string; labelFr: string }>;
  };
}

/**
 * Admin: Get staff profile for editing
 */
export async function adminGetStaffProfile(staffUserId: string): Promise<StaffProfileResponse | null> {
  return apiGet<StaffProfileResponse>(`/profile/admin/staff/${staffUserId}`);
}

/**
 * Admin: Update staff profile directly
 */
export async function adminUpdateStaffProfile(
  staffUserId: string,
  profileData: StaffProfileData,
): Promise<StaffProfileResponse | null> {
  return apiPut<StaffProfileResponse>(`/profile/admin/staff/${staffUserId}`, profileData);
}

/**
 * Admin: Upload a document for a staff member
 */
export async function adminUploadStaffDocument(
  staffUserId: string,
  documentType: StudentDocumentType,
  file: File,
): Promise<StudentDocument> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('documentType', documentType);

  return apiPostMultipart<StudentDocument>(`/profile/admin/staff/${staffUserId}/documents`, formData);
}

/**
 * Admin: Delete a document from a staff member's profile
 */
export async function adminDeleteStaffDocument(
  staffUserId: string,
  documentId: string,
): Promise<{ success: boolean }> {
  return apiDelete<{ success: boolean }>(`/profile/admin/staff/${staffUserId}/documents/${documentId}`);
}
